import React from "react";
import { FulfillmentStatus } from "@lib/ShiOneClient";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import { OrdersBoxIcon } from "@features/orders-features/components/OrdersBoxIcon";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptLong from "@mui/icons-material/ReceiptLong";
import ReceiptLongOff from "@mui/icons-material/ReceiptLongOutlined";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import PendingOutlined from "@mui/icons-material/PendingOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import shiPalette from "shared-ui/src/theme/shiPalette";
import { ReplayCircleFilled } from "@mui/icons-material";

export type OrderStatusChipStyle = {
  color: string;
  icon: React.ReactElement;
  iconColor?: string;
  borderColor?: string;
};

export function getOrderStatusChip(
  status: FulfillmentStatus | undefined,
): OrderStatusChipStyle {
  switch (status) {
    case FulfillmentStatus.AwaitingApproval:
      return {
        color: "warning.main",
        icon: <HowToRegIcon fontSize="small" sx={{ pl: 0.5 }} />,
        iconColor: "error.light",
        borderColor: "error.light",
      };
    case FulfillmentStatus.OrderPlaced:
      return {
        color: "inherit.main",
        icon: <OrdersBoxIcon fontSize="small" sx={{ pl: 0.5 }} />,
      };
    case FulfillmentStatus.Processing:
      return {
        color: "primary.light",
        icon: <InventoryIcon fontSize="small" sx={{ pl: 0.5 }} />,
      };
    case FulfillmentStatus.PartiallyShipped:
    case FulfillmentStatus.Shipped:
      return {
        color: "secondary.light",
        icon: <LocalShippingIcon fontSize="small" sx={{ pl: 0.5 }} />,
      };
    case FulfillmentStatus.Delivered:
      return {
        color: shiPalette.success["50p"],
        icon: <ApartmentOutlinedIcon fontSize="small" sx={{ pl: 0.5 }} />,
        iconColor: shiPalette.success.light,
      };
    case FulfillmentStatus.Returned:
      return {
        color: "text.inherit",
        icon: <ReplayCircleFilled fontSize="small" sx={{ pl: 0.5 }} />,
      };
    case FulfillmentStatus.Cancelled:
      return {
        color: "text.inherit",
        icon: <CancelOutlined fontSize="small" sx={{ pl: 0.5 }} />,
      };
    case FulfillmentStatus.Denied:
      return {
        color: "text.primary",
        icon: <BlockOutlined fontSize="small" sx={{ pl: 0.5 }} />,
        iconColor: "error.light",
        borderColor: "error.light",
      };
    // TODO: implement specific colors/icons for these statuses in the future
    case FulfillmentStatus.TransferApprover:
    case FulfillmentStatus.ApprovalExpired:
    case FulfillmentStatus.PoRequested:
    case FulfillmentStatus.PendingReturn:
    default:
      return {
        color: "text.secondary",
        icon: <HourglassEmptyOutlinedIcon fontSize="small" sx={{ pl: 0.5 }} />,
      };
  }
}

export function getInvoiceStatusChip(status: string | undefined): {
  icon: React.ElementType | undefined;
  borderColor: string;
  iconColor: string;
} {
  const s = (status ?? "").toLowerCase();

  if (s === "invoiced")
    return {
      icon: ReceiptLong,
      borderColor: shiPalette.success["50p"],
      iconColor: shiPalette.success.light,
    };

  if (s === "pending")
    return {
      icon: PendingOutlined,
      borderColor: shiPalette.warning["50p"],
      iconColor: shiPalette.warning.light,
    };

  if (s === "not invoiced")
    return {
      icon: ReceiptLongOff,
      borderColor: shiPalette.action.disabled,
      iconColor: shiPalette.text.secondary,
    };

  if (s === "cancelled" || s === "canceled")
    return {
      icon: CancelOutlined,
      borderColor: shiPalette.action.disabled,
      iconColor: shiPalette.text.secondary,
    };

  return {
    icon: undefined,
    borderColor: shiPalette.action.disabled,
    iconColor: shiPalette.text.secondary,
  };
}
